import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";

import polycarbonateImage from "@/assets/polycarbonate-optimized.webp";
import biokeramicImage from "@/assets/biokeramiska-optimized.webp";
import novaImage from "@/assets/nova-optimized.webp";

const content = {
  sv: {
    title: "Tre kupoltyper för olika behov",
    subtitle: "Funktionella lösningar för olika användningsområden",
    polycarbonate: {
      title: "Panoramakupoler",
      description: "För växthus, uterum, kontor, boende, glamping, restaurang, event och publika miljöer. Transparenta kupoler som ger en unik upplevelse med full kontakt med omgivningen.",
      features: ["Transparens och ljusinsläpp", "Flexibilitet i användning", "10-30 m² – anpassade för attefallsreglerna"],
      cta: "Utforska panoramakupoler"
    },
    bioceramic: {
      title: "Biokeramiska kupoler",
      description: "För boende, studios och regenerativa communities. Hållbara kupoler byggda för lång livslängd och helårsboende med fokus på miljövänliga material.",
      features: ["Hållbara material med minimal miljöpåverkan", "500 års livslängd", "Princip underhållsfritt"],
      cta: "Utforska biokeramiska"
    },
    nova: {
      title: "Nova Domes",
      description: "Kupoler utvecklade för extrema miljöer såsom skidresorter och utsatta platser. Anpassade för snö, vind och tuffa klimatförhållanden.",
      features: ["Kupoler för extrema klimat", "Snö- och vindtåliga", "Enkel & snabb montering"],
      cta: "Utforska Nova Domes"
    }
  },
  en: {
    title: "Three dome types for different needs",
    subtitle: "Functional solutions for different applications",
    polycarbonate: {
      title: "Panorama Domes",
      description: "For greenhouses, sunrooms, offices, living, glamping, restaurants, events and public spaces. Transparent domes that provide a unique experience with full contact with the surroundings.",
      features: ["Transparency and light", "Flexibility in use", "10-30 m² – Attefall-compliant sizes"],
      cta: "Explore panorama domes"
    },
    bioceramic: {
      title: "Bioceramic Domes",
      description: "For living, studios and regenerative communities. Sustainable domes built for longevity and year-round living with a focus on eco-friendly materials.",
      features: ["Sustainable materials with minimal environmental impact", "500 year lifespan", "Practically maintenance-free"],
      cta: "Explore bioceramic"
    },
    nova: {
      title: "Nova Domes",
      description: "Domes developed for extreme environments such as ski resorts and exposed locations. Adapted for snow, wind and tough climate conditions.",
      features: ["Domes for extreme climates", "Snow and wind resistant", "Quick & easy assembly"],
      cta: "Explore Nova Domes"
    }
  },
  de: {
    title: "Drei Kuppeltypen für verschiedene Bedürfnisse",
    subtitle: "Funktionelle Lösungen für verschiedene Anwendungen",
    polycarbonate: {
      title: "Panorama-Kuppeln",
      description: "Für Gewächshäuser, Wintergärten, Büros, Wohnen, Glamping, Restaurants, Veranstaltungen und öffentliche Räume. Transparente Kuppeln, die ein einzigartiges Erlebnis mit vollem Kontakt zur Umgebung bieten.",
      features: ["Transparenz und Licht", "Flexibilität in der Nutzung", "10-30 m² – Attefall-konforme Größen"],
      cta: "Panorama-Kuppeln erkunden"
    },
    bioceramic: {
      title: "Biokeramische Kuppeln",
      description: "Für Wohnen, Studios und regenerative Gemeinschaften. Nachhaltige Kuppeln, die für Langlebigkeit und ganzjähriges Wohnen mit Fokus auf umweltfreundliche Materialien gebaut sind.",
      features: ["Nachhaltige Materialien mit minimaler Umweltbelastung", "500 Jahre Lebensdauer", "Praktisch wartungsfrei"],
      cta: "Biokeramische erkunden"
    },
    nova: {
      title: "Nova Domes",
      description: "Kuppeln, die für extreme Umgebungen wie Skigebiete und exponierte Standorte entwickelt wurden. Angepasst an Schnee, Wind und harte Klimabedingungen.",
      features: ["Kuppeln für extreme Klimazonen", "Schnee- und windbeständig", "Einfache & schnelle Montage"],
      cta: "Nova Domes erkunden"
    }
  }
};

export const ProductsOverview = () => {
  const { language } = useLanguage();
  const t = content[language];

  const products = [
    {
      ...t.polycarbonate,
      link: "/panoramakupoler",
      image: polycarbonateImage,
      altText: {
        sv: "Panoramakupol – transparent geodetisk kupol från Diamond Domes",
        en: "Panorama dome – transparent geodesic dome from Diamond Domes",
        de: "Panorama-Kuppel – transparente geodätische Kuppel von Diamond Domes",
      }[language]
    },
    {
      ...t.bioceramic,
      link: "/biokeramiska-domes",
      image: biokeramicImage,
      altText: {
        sv: "Biokeramisk kupol för hållbart boende – Diamond Domes",
        en: "Bioceramic dome for sustainable living – Diamond Domes",
        de: "Biokeramische Kuppel für nachhaltiges Wohnen – Diamond Domes",
      }[language]
    },
    {
      ...t.nova,
      link: "/nova-domes",
      image: novaImage,
      altText: {
        sv: "Nova Dome – geodetisk kupol för extrema klimat",
        en: "Nova Dome – geodesic dome for extreme climates",
        de: "Nova Dome – geodätische Kuppel für extreme Klimazonen",
      }[language]
    }
  ];

  return (
    <section id="products" className="py-20 px-4 md:px-6 gradient-soft">
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12 animate-slide-up">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-foreground">
            {t.title}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>
        
        <div className="grid md:grid-cols-3 gap-8">
          {products.map((product, index) => (
            <article 
              key={index}
              className="bg-background rounded-xl overflow-hidden shadow-elegant hover:shadow-glow transition-smooth animate-slide-up group"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src={product.image} 
                  alt={product.altText}
                  className="w-full h-full object-cover group-hover:scale-105 transition-smooth"
                  loading="lazy"
                  decoding="async"
                  width="400"
                  height="300"
                  sizes="(max-width: 768px) 100vw, 33vw"
                />
              </div>
              <div className="p-6 text-center md:text-left">
                <h3 className="text-xl font-serif font-bold mb-3 text-foreground">
                  {product.title}
                </h3>
                <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
                  {product.description}
                </p>
                <ul className="space-y-1 mb-4">
                  {product.features.map((feature, i) => (
                    <li key={i} className="text-sm text-muted-foreground flex items-center justify-center md:justify-start">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="flex justify-center md:justify-start">
                  <Link 
                    to={product.link}
                    className="inline-flex items-center px-3 py-1.5 bg-primary text-primary-foreground rounded text-xs font-medium hover:bg-primary/90 transition-colors"
                  >
                    {product.cta}
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};
